// ── Creator Support System ─────────────────────────────────────────
// Replaces the complex crowdfunding system with three simple options:
//   1. Tip — one-time BCH tip (handled by BCHTipButton)
//   2. Membership — monthly BCH membership with supporter badge
//   3. Creator Goal — one active funding goal at a time

import { PLATFORM_FEE_ADDRESS } from './platform';
export { PLATFORM_FEE_ADDRESS };

// ── Nostr Event Kinds ─────────────────────────────────────────────
// Kind 30078 = addressable/replaceable app-specific data

/** Membership tier definition published by a creator. One per creator (replaceable). */
export const MEMBERSHIP_KIND = 30078;
export const MEMBERSHIP_D_TAG = 'bchnostr/membership';
export const MEMBERSHIP_TAG = 'bch-membership';

/** Membership subscription event published by a supporter. */
export const SUBSCRIPTION_KIND = 9735; // reuse tip receipt for social proof
export const SUBSCRIPTION_TAG = 'bch-membership-sub';

/** Creator Goal definition published by a creator. One active at a time (replaceable). */
export const GOAL_KIND = 30078;
export const GOAL_D_TAG = 'bchnostr/creator-goal';
export const GOAL_TAG = 'bch-creator-goal';

/**
 * Archived (completed) Creator Goal. The active goal above is REPLACEABLE
 * (fixed d-tag) so starting a new goal permanently overwrites the old one and
 * its history is lost. To preserve a creator's finished campaigns, we snapshot
 * each completed goal into its own addressable event with a UNIQUE d-tag
 * derived from the goal's stable `createdAt`, so every past goal persists and
 * can be listed. Reuses GOAL_KIND (30078) and keeps the `bch-creator-goal`
 * `t` tag so pledges (keyed by `goal_created`) still resolve their totals.
 */
export const GOAL_ARCHIVE_TAG = 'bch-creator-goal-archive';
export const GOAL_ARCHIVE_D_PREFIX = 'bchnostr/creator-goal-archive/';

/** Build the unique d-tag for an archived goal from its stable created_at. */
export function goalArchiveDTag(goalCreatedAt: number): string {
  return `${GOAL_ARCHIVE_D_PREFIX}${goalCreatedAt}`;
}

/** Support pledge for a creator goal (reuses tip receipt kind for social proof). */
export const GOAL_PLEDGE_KIND = 9735;
export const GOAL_PLEDGE_TAG = 'bch-goal-pledge';

// ── Platform fee ──────────────────────────────────────────────────
export const SUPPORT_FEE_PERCENT = 0.03; // 3% of each support payment

// ── Membership Types ──────────────────────────────────────────────
export interface MembershipData {
  /** Monthly price in USD */
  priceUsd: number;
  /** Short description of membership benefits */
  description: string;
  /** Whether the creator has an active membership offering */
  active: boolean;
  /** Timestamp when membership was set up */
  createdAt: number;
}

export interface MembershipSubscription {
  /** Pubkey of the creator */
  creatorPubkey: string;
  /** Month the subscription covers (YYYY-MM) */
  month: string;
  /** Amount paid in sats */
  amountSats: number;
  /** BCH txid */
  txid: string;
}

// ── Creator Goal Types ────────────────────────────────────────────
export interface CreatorGoalData {
  /** Goal title */
  title: string;
  /** Description of what the funds will be used for */
  description: string;
  /** Target amount in USD */
  targetUsd: number;
  /** Whether the goal is currently active */
  active: boolean;
  /** Unix timestamp when goal was created */
  createdAt: number;
  /** Unix timestamp when goal was completed (if completed) */
  completedAt?: number;
  /** Optional cover image URL (https only) shown on the goal card */
  image?: string;
}

export interface GoalPledge {
  /** Creator pubkey */
  creatorPubkey: string;
  /** Goal event ID */
  goalEventId: string;
  /** Amount in sats */
  amountSats: number;
  /** BCH txid */
  txid: string;
}

// ── Helpers ───────────────────────────────────────────────────────

/** Convert USD to sats using current BCH price */
export function usdToSatsMembership(usd: number, bchPrice: number | null): number {
  if (!bchPrice || bchPrice <= 0) return 0;
  return Math.round((usd / bchPrice) * 1e8);
}

/** Format a sats amount as BCH */
export function satsToDisplay(sats: number, bchPrice: number | null): string {
  const bch = sats / 1e8;
  const usd = bchPrice ? (bch * bchPrice).toFixed(2) : null;
  const bchStr = bch < 0.001
    ? `${sats.toLocaleString()} sats`
    : `${bch.toFixed(4)} BCH`;
  return usd ? `${bchStr} (~$${usd})` : bchStr;
}

/** Calculate progress percentage for a creator goal */
export function goalProgress(raisedSats: number, targetUsd: number, bchPrice: number | null): number {
  if (!bchPrice || targetUsd <= 0) return 0;
  const targetSats = usdToSatsMembership(targetUsd, bchPrice);
  if (targetSats <= 0) return 0;
  return Math.min(100, Math.round((raisedSats / targetSats) * 100));
}

/** Check if a subscription covers the current month */
export function isActiveSubscriber(
  subscriptions: { month: string; creatorPubkey: string }[],
  creatorPubkey: string,
): boolean {
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  return subscriptions.some(
    s => s.creatorPubkey === creatorPubkey && s.month === currentMonth,
  );
}

/** Get the current month string for subscription */
export function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

